import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { ethers } from "ethers";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { useAppKitAccount } from "@reown/appkit/react";
import StakingABI from "../abi/staking_abi.json";
import IERC20ABI from "../abi/ERC20_ABI.json";
import {
  stakingContract,
  usdtContractAddress,
  cranniAddress,
  lpToken,
  provider,
  wsprovider,
  bckpProvider,
  CRANNILuckBlocksAddress,
  maticLuckBlocksAddress,
} from "../.config";
import { TrendingUp, Coins, Gift, Droplet, AlertTriangle } from "lucide-react";
import CRANNILuckBlocks from "../abi/CRANNILuckblocks.json";
import MaticLuckBlocks from "../abi/MATICLuckBlocks.json";
import lpTokenABI from "../abi/LPtoken_abi.json";
import MintBurn from "../components/MintBurn/MintBurn";
import { useWriteContract, useReadContract, useReadContracts } from "wagmi";

const StatCard = ({ title, value, icon: Icon, className = "", onClaim, address, rewards }) => (
  <div
    className={`bg-gradient-to-b from-[#010101] to-[#00102a] backdrop-blur-sm border border-purple-500/30 rounded-xl p-6 flex items-center gap-4 hover:bg-purple-900/30 transition-all duration-300 ${className}`}
  >
    <div className="p-3 bg-purple-500/20 rounded-lg border border-purple-500/30">
      {Icon}
    </div>
    <div className="flex-1 flex justify-between items-center">
      <div>
        <p className="text-sm font-medium text-purple-300/80">{title}</p>
        <p className="text-lg font-bold text-white mt-1">{value}</p>
      </div>
      {title === "Pending Rewards" && address && rewards > 0 && (
        <button
          onClick={onClaim}
          className="ml-4 px-4 py-2 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 rounded-lg text-sm font-bold transition-all duration-300"
        >
          Claim
        </button>
      )}
    </div>
  </div>
);

const DebtWarning = ({ userUnpaidRewards }) => {
  if (userUnpaidRewards <= 0) return null;
  
  return (
    <div className="bg-gradient-to-r from-yellow-900/20 to-red-900/20 border border-yellow-500/30 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-3">
        <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
          <AlertTriangle size={16} className="text-black" />
        </div>
        <div>
          <h4 className="text-yellow-400 font-bold">Unpaid Rewards Notice</h4>
          <p className="text-yellow-200 text-sm">
            You have {userUnpaidRewards} USDT in unpaid rewards. These will be automatically paid when the contract receives new reward tokens.
          </p>
        </div>
      </div>
    </div>
  );
};

const Token = () => {
  const { address, isConnected } = useAppKitAccount();
  const [pendingRewards, setPendingRewards] = useState(0);
  const [inputValue, setInputValue] = useState("0");
  const [amount, setAmount] = useState("0");
  const [totalRewardBalance, setTotalRewardBalance] = useState(0);
  const [pendingRewardTotal, setPendingRewardTotal] = useState(0);
  const [userStakedAmount, setUserStakedAmount] = useState(0);
  const [rewardPerBlock, setRewardPerBlock] = useState(0);
  const [rewardBalance, setRewardBalance] = useState(0);
  const [totalStaked, setTotalStaked] = useState(0);
  const [pendPenalization, setPendPenalization] = useState(0);
  const [lpTokenBalance, setLpTokenBalance] = useState(0);
  const [apr, setApr] = useState(0);
  const [isDepositMode, setIsDepositMode] = useState(true);
  const [cranniPrice, setCranniPrice] = useState(0);
  const [cranniMarketCap, setCranniMarketCap] = useState(0);
  const [cranniTotalSupply, setCranniTotalSupply] = useState(0);
  const [polPrice, setPolPrice] = useState(0);
  const [lpPrice, setLPPrice] = useState(0);
  
  // New debt system state variables
  const [totalUnpaidRewards, setTotalUnpaidRewards] = useState(0);
  const [userUnpaidRewards, setUserUnpaidRewards] = useState(0);
  const [availableRewards, setAvailableRewards] = useState(0);
  
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { writeContractAsync } = useWriteContract();

  const getInitialSection = () => {
    const section = searchParams.get('section');
    if (section && ['token', 'staking', 'cynths'].includes(section)) {
      return section === 'staking' ? 'stakingDashboard' : section;
    }

    if (location.state?.activeSection) {
      return location.state.activeSection;
    }

    return "token";
  };

  const [activeSection, setActiveSection] = useState(getInitialSection());

  useEffect(() => {
    const section = searchParams.get('section');
    if (section && ['token', 'staking', 'cynths'].includes(section)) {
      const mappedSection = section === 'staking' ? 'stakingDashboard' : section;
      setActiveSection(mappedSection);
    }
  }, [searchParams]);

  // Fetch prices using useReadContract
  const { data: cranniPriceData } = useReadContract({
    address: CRANNILuckBlocksAddress,
    abi: CRANNILuckBlocks.abi,
    functionName: "getLatestPrice",
  });

  const { data: maticPriceData } = useReadContract({
    address: maticLuckBlocksAddress,
    abi: MaticLuckBlocks.abi,
    functionName: "getLatestPrice",
  });

  // Process prices
  useEffect(() => {
    if (cranniPriceData && maticPriceData) {
      const cranniCoinPrice = parseFloat(ethers.utils.formatUnits(cranniPriceData, 6));
      const maticCoinPrice = parseFloat(ethers.utils.formatUnits(maticPriceData, 18));
      setCranniPrice(cranniCoinPrice.toFixed(3));
      setPolPrice(maticCoinPrice.toFixed(2));
      const mkcap = (cranniCoinPrice * 8888888).toFixed(2);
      setCranniMarketCap(Number(mkcap));
      setCranniTotalSupply(8888888);
    }
  }, [cranniPriceData, maticPriceData]);

  // Fetch LP Token Balance
  const { data: lpTokenBalanceData } = useReadContract({
    address: lpToken,
    abi: IERC20ABI.abi,
    functionName: "balanceOf",
    args: [address],
    watch: true,
    enabled: !!address && isConnected,
    pollingInterval: 15000,
  });

  useEffect(() => {
    if (lpTokenBalanceData) {
      const formattedBalance = ethers.utils.formatUnits(lpTokenBalanceData, 18);
      setLpTokenBalance(parseFloat(formattedBalance).toFixed(18));
    }
  }, [lpTokenBalanceData]);

  // Fetch contract data
  const { data: contractData } = useReadContracts({
    contracts: [
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "totalRewardBalance",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "rewardPerBlock",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "rewardBalance",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "pendingRewardTotal",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "totalStaked",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      ...(address
        ? [
          {
            address: stakingContract,
            abi: StakingABI.abi,
            functionName: "pendingPenalization",
            args: [address],
            watch: true,
            enabled: !!address && isConnected,
            pollingInterval: 15000,
          },
        ]
        : []),
    ],
  });

  useEffect(() => {
    if (contractData && isConnected && address) {
      const [totalReward, rewardBlock, rewardBal, pendingRewardTotal, totalStakedAmt, penalizationTime] =
        contractData.map((d) => d.result);

      setTotalRewardBalance(Number(ethers.utils.formatUnits(totalReward || 0, 6)).toFixed(3));
      setRewardPerBlock(ethers.utils.formatUnits(rewardBlock || 0, 6));
      setRewardBalance(Number(ethers.utils.formatUnits(rewardBal || 0, 6)).toFixed(3));
      setPendingRewardTotal(ethers.utils.formatUnits(pendingRewardTotal || 0, 6));
      setTotalStaked(ethers.utils.formatUnits(totalStakedAmt || 0, 18));

      if (address && penalizationTime) {
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const penaltyEndTimestamp = Number(penalizationTime);
        const timeDifference = penaltyEndTimestamp - currentTimestamp;
        processPenalization(timeDifference);
      }
    } else if (!isConnected || !address) {
      setRewardBalance(0);
      setPendPenalization("0");
    }
  }, [contractData, address, isConnected]);

  // Fetch user staked amount
  const { data: userInfo } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "userInfo",
    args: [0, address],
    watch: true,
    enabled: !!address && isConnected,
    pollingInterval: 15000,
  });

  useEffect(() => {
    if (userInfo) {
      const formattedAmount = ethers.utils.formatUnits(userInfo[1], 18);
      setUserStakedAmount(formattedAmount);
    }
  }, [userInfo]);

  // Fetch pending rewards with new debt system format
  const { data: rewards } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "pendingReward",
    args: [address],
    watch: true,
    enabled: !!address && isConnected,
    pollingInterval: 15000,
  });

  useEffect(() => {
    if (rewards && rewards.length === 3) {
      // New contract returns [totalPending, availableNow, unpaidDebt]
      const [totalPending, availableNow, unpaidDebt] = rewards;
      setPendingRewards(Number(ethers.utils.formatUnits(totalPending, 6)).toFixed(3));
      setAvailableRewards(Number(ethers.utils.formatUnits(availableNow, 6)).toFixed(3));
      setUserUnpaidRewards(Number(ethers.utils.formatUnits(unpaidDebt, 6)).toFixed(3));
    } else if (rewards) {
      // Fallback for old contract format
      const formattedRewards = ethers.utils.formatUnits(rewards, 6);
      setPendingRewards(formattedRewards);
      setAvailableRewards(formattedRewards);
      setUserUnpaidRewards(0);
    }
  }, [rewards]);

  // Fetch total debt information
  const { data: totalDebt } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "getTotalDebt",
    watch: true,
    enabled: !!address && isConnected,
  });

  const { data: userDebt } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "getUserUnpaidRewards",
    args: [address],
    watch: true,
    enabled: !!address && isConnected,
  });

  useEffect(() => {
    if (totalDebt) {
      setTotalUnpaidRewards(Number(ethers.utils.formatUnits(totalDebt, 6)).toFixed(3));
    }
  }, [totalDebt]);

  useEffect(() => {
    if (userDebt) {
      setUserUnpaidRewards(Number(ethers.utils.formatUnits(userDebt, 6)).toFixed(3));
    }
  }, [userDebt]);

  // Fetch LP Token data for APR calculation
  const { data: token0 } = useReadContract({
    address: lpToken,
    abi: lpTokenABI.abi,
    functionName: "token0",
  });

  const { data: token1 } = useReadContract({
    address: lpToken,
    abi: lpTokenABI.abi,
    functionName: "token1",
  });

  const { data: reserves } = useReadContract({
    address: lpToken,
    abi: lpTokenABI.abi,
    functionName: "getReserves",
  });

  const { data: token0Decimals } = useReadContract({
    address: token0,
    abi: IERC20ABI.abi,
    functionName: "decimals",
    enabled: !!token0,
  });

  const { data: token1Decimals } = useReadContract({
    address: token1,
    abi: IERC20ABI.abi,
    functionName: "decimals",
    enabled: !!token1,
  });

  const { data: totalSupply } = useReadContract({
    address: lpToken,
    abi: lpTokenABI.abi,
    functionName: "totalSupply",
  });

  // Calculate APR
  useEffect(() => {
    if (token0 && token1 && reserves && token0Decimals && token1Decimals && totalSupply && polPrice && cranniPrice) {
      try {
        const reserve0 = ethers.utils.formatUnits(reserves[0], token0Decimals);
        const reserve1 = ethers.utils.formatUnits(reserves[1], token1Decimals);
        const token0Price = parseFloat(polPrice);
        const token1Price = parseFloat(cranniPrice);
        const liquidityValue = parseFloat(reserve0) * token0Price + parseFloat(reserve1) * token1Price;
        const formattedTotalSupply = ethers.utils.formatUnits(totalSupply, 18);
        const lpTokenPrice = liquidityValue / parseFloat(formattedTotalSupply);
        setLPPrice(lpTokenPrice.toFixed(2));

        const BLOCKS_PER_YEAR = (365 * 24 * 60 * 60) / 3;
        const rewardTokenPrice = 1; // Assuming USDT price is 1
        const yearlyRewards = parseFloat(rewardPerBlock) * BLOCKS_PER_YEAR * rewardTokenPrice;
        const totalStakedValue = parseFloat(totalStaked) * lpTokenPrice;
        if (totalStakedValue > 0) {
          const calculatedApr = (yearlyRewards / totalStakedValue) * 100;
          setApr(calculatedApr.toFixed(2));
        } else {
          setApr(0);
        }
      } catch (err) {
        console.error("Error calculating APR:", err);
        setApr(0);
      }
    }
  }, [token0, token1, reserves, token0Decimals, token1Decimals, totalSupply, polPrice, cranniPrice, rewardPerBlock, totalStaked]);

  // Existing contract read hooks
  const { data: allowanceAutoBuy } = useReadContract({
    address: cranniAddress,
    abi: IERC20ABI.abi,
    functionName: "allowance",
    args: [address, cranniAddress],
    watch: true,
    enabled: !!address && isConnected,
  });

  const { data: lowerAmount } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "lowerAmount",
    watch: true,
    enabled: !!address && isConnected,
  });

  const { data: allowanceDeposit } = useReadContract({
    address: lpToken,
    abi: IERC20ABI.abi,
    functionName: "allowance",
    args: [address, stakingContract],
    watch: true,
    enabled: !!address && isConnected,
  });

  useEffect(() => {
    if (!isConnected || !address) {
      setPendingRewards(0);
      setUserStakedAmount(0);
      setLpTokenBalance(0);
      setUserUnpaidRewards(0);
      setAvailableRewards(0);
    }
  }, [isConnected, address]);

  const processPenalization = (timeDifference) => {
    if (timeDifference > 0) {
      if (timeDifference >= 86400) {
        const days = Math.ceil(timeDifference / 86400);
        setPendPenalization(`${days} day${days > 1 ? "s" : ""}`);
      } else {
        const hours = Math.floor(timeDifference / 3600);
        const minutes = Math.floor((timeDifference % 3600) / 60);
        const seconds = timeDifference % 60;
        setPendPenalization(`${hours}h ${minutes}m ${seconds}s`);
      }
    } else {
      setPendPenalization("0");
    }
  };

  const setMaxAmount = () => {
    if (isDepositMode) {
      setAmount(lpTokenBalance);
    } else {
      setAmount(userStakedAmount);
    }
  };

  const approveForAutoBuy = async () => {
    if (!address) {
      Swal.fire({
        icon: "warning",
        title: "Not Connected",
        text: "Connect your Wallet to approve for Direct Automated Buy/Sell",
        timer: 2000,
        timerProgressBar: true,
      });
      return;
    }

    if (allowanceAutoBuy > 0) {
      Swal.fire({
        icon: "success",
        title: "Already Approved",
        text: "Already approved contract token for Direct Automated Buy/Sell",
        timer: 2000,
        timerProgressBar: true,
      });
      return;
    }

    try {
      const approveHash = await writeContractAsync({
        address: cranniAddress,
        abi: IERC20ABI.abi,
        functionName: "approve",
        args: [cranniAddress, ethers.constants.MaxUint256],
      });

      const approveReceipt = await provider.waitForTransaction(approveHash);
      if (!approveReceipt.status) throw new Error("Approval transaction failed.");

      Swal.fire({
        icon: "success",
        title: "Approval Successful",
        text: "Approved contract token for Direct Automated Buy/Sell",
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (err) {
      console.error("Error during approval:", err);
      Swal.fire("Error", "Approval failed. Try again later.", "error");
    }
  };

  const handleDeposit = async () => {
    if (!amount) return Swal.fire("Error", "Please enter an amount", "error");
    try {
      const parsedAmount = ethers.utils.parseUnits(amount, 18);

      if (parsedAmount.lt(lowerAmount)) {
        return Swal.fire(
          "Error",
          `Minimum deposit amount is ${ethers.utils.formatUnits(lowerAmount, 18)}`,
          "error"
        );
      }

      if (allowanceDeposit === 0n) {
        const approveHash = await writeContractAsync({
          address: lpToken,
          abi: IERC20ABI.abi,
          functionName: "approve",
          args: [stakingContract, ethers.constants.MaxUint256],
        });

        const approveReceipt = await provider.waitForTransaction(approveHash);
        if (!approveReceipt.status) throw new Error("Approval transaction failed.");

        Swal.fire({
          icon: "success",
          title: "Approval Successful",
          text: "LP Token has been approved for staking",
          timer: 2000,
          timerProgressBar: true,
        });
      }

      const depositHash = await writeContractAsync({
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "deposit",
        args: [parsedAmount],
      });

      const depositReceipt = await provider.waitForTransaction(depositHash);
      if (!depositReceipt.status) throw new Error("Deposit transaction failed.");

      setAmount("0");
      setUserStakedAmount((prev) => (parseFloat(prev) + parseFloat(amount)).toFixed(18));
      setLpTokenBalance((prev) => (parseFloat(prev) - parseFloat(amount)).toFixed(18));
      Swal.fire("Success", "Deposit successful", "success");
    } catch (err) {
      console.error("Error during deposit:", err);
      Swal.fire("Error", "Transaction failed. Try again later.", "error");
    }
  };

  const claimRewards = async () => {
    try {
      Swal.fire({
        title: 'Processing...',
        text: 'Claiming your rewards',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      const withdrawHash = await writeContractAsync({
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "withdraw",
        args: [0],
      });

      const withdrawReceipt = await provider.waitForTransaction(withdrawHash);
      if (!withdrawReceipt.status) throw new Error("Withdrawal transaction failed.");

      // The contract will handle partial payments automatically
      const claimedAmount = parseFloat(availableRewards);
      
      Swal.fire({
        icon: "success",
        title: "Rewards Claimed Successfully!",
        text: claimedAmount > 0 
          ? `You claimed ~${claimedAmount.toFixed(6)} USDT${userUnpaidRewards > 0 ? '. Remaining debt will be paid when rewards are available.' : ''}`
          : "Claim recorded. Rewards will be paid when available.",
        timer: 4000,
        timerProgressBar: true,
      });

    } catch (err) {
      console.error("Error during rewards withdrawal:", err);
      Swal.fire("Error", "Transaction failed. Try again later.", "error");
    }
  };

  const handleWithdraw = async () => {
    if (!amount) amount = 0;

    try {
      const parsedAmount = ethers.utils.parseUnits(amount, 18);

      if (pendPenalization && pendPenalization !== "0" && amount > 0) {
        const result = await Swal.fire({
          title: "Withdrawal Penalty Warning",
          html: `You currently have a withdrawal penalty period of ${pendPenalization}.<br>
                 Proceeding with withdrawal now will result in a 10% penalty fee.<br><br>
                 Do you want to continue?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, withdraw anyway",
          cancelButtonText: "No, cancel",
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
        });

        if (!result.isConfirmed) return;
      }

      const withdrawHash = await writeContractAsync({
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "withdraw",
        args: [parsedAmount],
      });

      const withdrawReceipt = await provider.waitForTransaction(withdrawHash);
      if (!withdrawReceipt.status) throw new Error("Withdrawal transaction failed.");

      setAmount("0");
      setUserStakedAmount((prev) => (parseFloat(prev) - parseFloat(amount)).toFixed(18));
      setLpTokenBalance((prev) => (parseFloat(prev) + parseFloat(amount)).toFixed(18));
      Swal.fire("Success", "Withdrawal successful", "success");
    } catch (err) {
      console.error("Error during withdrawal:", err);
      Swal.fire("Error", "Transaction failed. Try again later.", "error");
    }
  };

  const SubMenu = () => (
    <div className="flex justify-center mb-8">
      <div className="backdrop-blur-sm border border-purple-500/30 rounded-xl inline-flex">
        <button
          onClick={() => setActiveSection("token")}
          className={`px-6 py-3 rounded-xl transition-all duration-300 font-bold text-center items-center justify-center rounded-[10px] text-md font-tcbregular italic ${activeSection === "token"
            ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            : "text-purple-300/60 hover:text-white"
            }`}
        >
          Token Info
        </button>
        <button
          onClick={() => setActiveSection("stakingDashboard")}
          className={`px-6 py-3 rounded-xl transition-all duration-300 font-bold text-center items-center justify-center rounded-[10px] text-md font-tcbregular italic ${activeSection === "stakingDashboard"
            ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            : "text-purple-300/60 hover:text-white"
            }`}
        >
          Staking Dashboard
        </button>
        <button
          onClick={() => setActiveSection("cynths")}
          className={`px-6 py-3 rounded-xl transition-all duration-300 font-bold text-center items-center justify-center rounded-[10px] text-md font-tcbregular italic ${activeSection === "cynths"
            ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            : "text-purple-300/60 hover:text-white"
            }`}
        >
          Cynths
        </button>
      </div>
    </div>
  );

  const CranniInfoSection = () => (
    <div className="bg-gradient-to-b from-[#010101] to-[#00102a] backdrop-blur-sm border border-purple-500/30 rounded-xl p-6 mt-6">
      <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-4">
        CRANNI Token
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-b from-[#010101] to-[#00102a] p-4 rounded-lg border border-purple-500/30">
          <p className="text-purple-300/60 text-sm">Current Price</p>
          <p className="text-white font-bold text-lg">${cranniPrice}</p>
        </div>
        <div className="bg-gradient-to-b from-[#010101] to-[#00102a] p-4 rounded-lg border border-purple-500/30">
          <p className="text-purple-300/60 text-sm">Market Cap</p>
          <p className="text-white font-bold text-lg">${cranniMarketCap.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-b from-[#010101] to-[#00102a] p-4 rounded-lg border border-purple-500/30">
          <p className="text-purple-300/60 text-sm">Total Supply</p>
          <p className="text-white font-bold text-lg">{cranniTotalSupply.toLocaleString()}</p>
        </div>
      </div>
      <div className="mt-4 text-center">
        <p className="text-purple-300/80 mb-4">
          CRANNI: Crypto Revenue and Node Network Income Token
        </p>
        <div className="flex justify-center gap-4">
          <a
            href="https://pancakeswap.finance/v2/add/tBNB/0x8C426Bc4506b4f7BfB21cF67A342e0E7EBe0980f"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-all duration-300 rounded-xl text-white font-bold"
          >
            Add Liquidity on SWAP DEX
          </a>
        </div>
      </div>
    </div>
  );

  const TokenInfoContent = () => (
    <div className="relative w-full mt-5 text-center">
      <div className="text-white">
        <div className="p-1 border-rounded text-lg leading-relaxed space-y-4">
          <p>
            Beyond traditional cryptocurrency utility, CRANNI serves as a
            versatile asset with multiple critical functions:
          </p>
          <ul className="list-disc list-inside text-left space-y-2">
            <li>
              <strong>Synthetic Asset Collateralization:</strong> Use CRANNI as
              premium collateral for creating and trading synthetic assets,
              enabling advanced financial derivatives
            </li>
            <li>
              <strong>Liquidity Pool Staking:</strong> Earn substantial revenue
              by providing liquidity and staking CRANNI-paired tokens in our
              advanced staking protocols
            </li>
            <li>
              <strong>Smart Contract Utility:</strong> Unlock automated
              functions and advanced features within our ecosystem's smart
              contract framework
            </li>
          </ul>
          <div className="playcard border border-purple-500/30 rounded-xl p-4 mt-6">
            <h3 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Revolutionary Buy/Sell Mechanism
            </h3>
            <p className="mb-4">
              CRANNI introduces a groundbreaking, wallet-native buying and
              selling mechanism directly through the smart contract:
            </p>
            <div className="bg-purple-900/50 p-4 rounded-lg">
              <p className="mb-2">
                <strong>Buying CRANNI:</strong>
                <br /> Simply send POL directly to the CRANNI smart contract
                address, and tokens will be automatically purchased from the
                decentralized liquidity pool.
              </p>
              <p className="mb-2">
                <strong>Selling CRANNI:</strong>
                <br /> - Send 0.01 POL to instantly sell your entire CRANNI balance
                <br /> - Send 0.005 POL to sell exactly half of your CRANNI tokens
                <br /> - Send 0.0025 POL to sell exactly 1/4 of your CRANNI tokens
              </p>
              <p className="italic text-purple-300">
                No additional steps, exchanges, or complex interfaces required –
                trade directly from your wallet!
              </p>
            </div>
            <button
              onClick={approveForAutoBuy}
              className="justify-content-center items-center mt-2 gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-all duration-300 rounded-xl text-white font-bold"
            >
              Approve for Auto Buy/Sell
            </button>
          </div>
          <p className="mt-4">
            Designed with a dynamic scarcity mechanism and strategic utility,
            CRANNI represents the next evolution of blockchain-powered financial
            instruments.
          </p>
        </div>
      </div>
    </div>
  );

  const inputRef = React.useRef();

  const StakingDashboardContent = () => (
    <div className="flex flex-col gap-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-2 mt-5">
          Staking Dashboard
        </h1>
        <p className="text-purple-300/60">
          Manage your Liquidity Tokens and receive ecosystem revenue rewards.
        </p>
      </div>
      
      <DebtWarning userUnpaidRewards={parseFloat(userUnpaidRewards)} />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="APR"
          value={`${apr}%`}
          icon={<TrendingUp color="#10b981" size={24} />}
          className="bg-green-900/20 border-green-500/30"
        />
        <StatCard
          title="Your Staked Amount"
          value={`${parseFloat(userStakedAmount).toFixed(8)} CRANNI-POL ($${parseFloat(
            userStakedAmount * lpPrice
          ).toFixed(2)})`}
          icon={<Coins color="#3b82f6" size={24} />}
        />
       <StatCard
          title="Pending Rewards"
          value={
            <div className="flex flex-col gap-1">
              <div className="text-lg font-bold text-white">
                {pendingRewards} USDT
              </div>
              {userUnpaidRewards > 0 && (
                <div className="flex flex-col text-xs">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    <span className="text-green-400">Available: {availableRewards} USDT</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                    <span className="text-amber-400">Pending: {userUnpaidRewards} USDT</span>
                  </div>
                </div>
              )}
            </div>
          }
          icon={<Gift color="#8b5cf6" size={24} />}
          onClaim={claimRewards}
          className="bg-green-900/20 border-green-500/30"
          address={address}
          rewards={pendingRewards}
        />
        <StatCard
          title="LP Token Balance"
          value={`${parseFloat(lpTokenBalance).toFixed(8)} CRANNI-POL ($${parseFloat(
            lpTokenBalance * lpPrice
          ).toFixed(2)})`}
          icon={<Droplet color="#ec4899" size={24} />}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-b from-[#010101] to-[#00102a] backdrop-blur-sm border border-purple-500/30 rounded-xl p-6">
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setIsDepositMode(true)}
              className={`flex-1 py-2 px-4 rounded-lg font-bold transition-all duration-300 ${isDepositMode
                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                : "text-purple-300/60 hover:text-white"
                }`}
            >
              Deposit
            </button>
            <button
              onClick={() => setIsDepositMode(false)}
              className={`flex-1 py-2 px-4 rounded-lg font-bold transition-all duration-300 ${!isDepositMode
                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                : "text-purple-300/60 hover:text-white"
                }`}
            >
              Withdraw
            </button>
          </div>
          <div className="space-y-4">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                placeholder={`Amount to ${isDepositMode ? "Deposit" : "Withdraw"}`}
                value={amount}
                autoFocus={inputRef.current === document.activeElement}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, "");
                  if (value !== amount) {
                    setAmount(value);
                    inputRef.current?.focus();
                  }
                }}
                className="w-full bg-purple-900/30 border border-purple-500/30 rounded-xl p-4 text-white placeholder:text-purple-300/40 focus:outline-none focus:ring-2 focus:ring-purple-500/40 pr-20"
              />
              <button
                onClick={setMaxAmount}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1 bg-purple-500/30 hover:bg-purple-500/50 rounded-lg text-sm font-bold text-purple-300 transition-all duration-300"
              >
                MAX
              </button>
            </div>
            <button
              onClick={isDepositMode ? handleDeposit : handleWithdraw}
              className={`w-full transition-all duration-300 rounded-xl py-4 text-white font-bold shadow-lg ${isDepositMode
                ? "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-purple-500/20"
                : "bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 shadow-red-500/20"
                }`}
            >
              {isDepositMode ? "Deposit" : "Withdraw"}
            </button>
          </div>
        </div>
        <div className="bg-gradient-to-b from-[#010101] to-[#00102a] backdrop-blur-sm border border-purple-500/30 rounded-xl p-6">
          <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-4">
            Statistics
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-purple-300/60">Total Staked CRANNI-POL LP (TVL):</span>
              <span className="text-white font-bold">
                {parseFloat(totalStaked).toFixed(3)} LP (${parseFloat(totalStaked * lpPrice).toFixed(2)})
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-purple-300/60">Contract Total Reward Balance:</span>
              <span className="text-white font-bold">{totalRewardBalance} USDT</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-purple-300/60">Available Reward Balance:</span>
              <span className="text-white font-bold">{rewardBalance} USDT</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-purple-300/60">Total Contract Debt:</span>
              <span className="text-white font-bold text-red-400">{totalUnpaidRewards} USDT</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-purple-300/60">Your Unpaid Rewards:</span>
              <span className="text-white font-bold text-red-400">{userUnpaidRewards} USDT</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-purple-300/60">Reward per Block:</span>
              <span className="text-white font-bold">{rewardPerBlock} USDT</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-purple-300/60">Your Withdraw Penalty Time (10% fee):</span>
              <span className="text-white font-bold">{pendPenalization}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b via-[#202e44] from-black to-[#596b87] p-8">
      <div className="max-w-7xl mx-auto">
        <SubMenu />
        {activeSection === "token" && <CranniInfoSection />}
        {activeSection === "token" && <TokenInfoContent />}
         {activeSection === "stakingDashboard" && <StakingDashboardContent />}
        {activeSection === "cynths" && <MintBurn />}
      </div>
    </div>
  );
};

export default Token;